import { DateTime } from 'luxon';

let zone = 'Europe/London';
/** The planner's timezone (from settings) – all times are shown in it. */
export function setZone(tz: string) {
  zone = tz;
}
export const getZone = () => zone;

export const dt = (ms: number) => DateTime.fromMillis(ms, { zone });
export const todayISO = () => DateTime.now().setZone(zone).toISODate()!;
export const dateOf = (ms: number) => dt(ms).toISODate()!;
export const fromISODate = (date: string) => DateTime.fromISO(date, { zone });

export const time = (ms: number) => dt(ms).toFormat('HH:mm');
export const dayLong = (date: string) => fromISODate(date).toFormat('cccc d LLLL');
export const dayShort = (ms: number) => dt(ms).toFormat('ccc d LLL');
export const dateTime = (ms: number) => dt(ms).toFormat('ccc d LLL, HH:mm');

export function duration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}
export const minutes = (min: number) => duration(min * 60_000);

/** "in 23 min", "in 2h 5m", "5 min ago" */
export function relative(ms: number, now = Date.now()): string {
  const diff = ms - now;
  const abs = Math.abs(diff);
  if (abs < 60_000) return diff >= 0 ? 'now' : 'just now';
  if (abs >= 36 * 3600_000) {
    const days = Math.round(abs / 86_400_000);
    return diff > 0 ? `in ${days} day${days === 1 ? '' : 's'}` : `${days} day${days === 1 ? '' : 's'} ago`;
  }
  return diff > 0 ? `in ${duration(abs)}` : `${duration(abs)} ago`;
}

/** "Today", "Tomorrow", "Fri 23 Oct" */
export function dayName(ms: number): string {
  const d = dt(ms).startOf('day');
  const t = DateTime.now().setZone(zone).startOf('day');
  const diff = Math.round(d.diff(t, 'days').days);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return d.toFormat(diff > 0 && diff < 7 ? 'cccc' : 'ccc d LLL');
}

/** "07:30" -> 450; bad input -> 0 */
export function parseHMSafe(hm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm ?? '');
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
}

export const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function daysLabel(days: number[]): string {
  const s = [...days].sort();
  if (s.length === 7) return 'Every day';
  if (s.join() === '1,2,3,4,5') return 'Weekdays';
  if (s.join() === '6,7') return 'Weekends';
  return s.map((d) => DAY_SHORT[d - 1]).join(' ');
}

export const MODE_LABEL: Record<string, string> = { walk: 'walk', cycle: 'cycle', drive: 'drive', transit: 'public transport' };

/** For <input type="datetime-local"> etc. */
export const toLocalInput = (ms: number) => dt(ms).toFormat("yyyy-LL-dd'T'HH:mm");
export const fromLocalInput = (v: string) => DateTime.fromISO(v, { zone }).toMillis();
export const combine = (date: string, hm: string) => DateTime.fromISO(`${date}T${hm}`, { zone }).toMillis();
